// Become an Affiliate — iOS HIG form (inset-grouped list of fields)

import { useState } from 'react';
import { APP_ID } from '@funderpro/shared';
import { getToken, API_BASE } from '../../lib/auth';
import { Card, ErrorBox, FormField, Loading, PrimaryButton, TextArea, ToggleRow } from './UIShell';
import { BrandLogo, FeatureIcon } from '../../components/Brand';

export function AffiliatePage() {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [discountCoupon, setDiscountCoupon] = useState('');
  const [affCode, setAffCode] = useState('');
  const [socialLink, setSocialLink] = useState('');
  const [info, setInfo] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);

  async function checkCode(code: string) {
    if (!/^[a-z0-9_-]{3,32}$/i.test(code)) { setCodeAvailable(null); return; }
    setChecking(true);
    try {
      const r = await fetch(`${API_BASE}/api/apps/${APP_ID}/affiliate/check-code/${encodeURIComponent(code)}`);
      const d = await r.json();
      setCodeAvailable(d.available);
    } catch { setCodeAvailable(null); }
    finally { setChecking(false); }
  }

  async function submit() {
    if (!email || !affCode) { setResult({ ok: false, msg: 'Email and affiliate code are required.' }); return; }
    if (!accepted) { setResult({ ok: false, msg: 'You must accept the agreement.' }); return; }
    if (codeAvailable === false) { setResult({ ok: false, msg: 'That affiliate code is already taken.' }); return; }
    setSubmitting(true); setResult(null);
    try {
      const t = getToken();
      const r = await fetch(`${API_BASE}/api/apps/${APP_ID}/affiliate/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({ email, full_name: fullName, discount_coupon: discountCoupon, aff_code: affCode, social_link: socialLink, info, accepted_terms: accepted }),
      });
      const d = await r.json();
      if (d?.error_type) setResult({ ok: false, msg: d.message || 'Failed to send request' });
      else setResult({ ok: true, msg: `Request sent! Your affiliate code is "${d.aff_code}". We'll review and get back to you.` });
    } catch (e: any) {
      setResult({ ok: false, msg: e?.message || 'Failed to send request' });
    } finally { setSubmitting(false); }
  }

  return (
    <div className="p-4">
      <h1 className="px-1 text-[34px] font-bold leading-[1.1] tracking-tight text-tf-text-1">Become an affiliate</h1>
      <p className="mt-1 px-1 text-[15px] text-tf-text-3">Give us more information about yourself</p>

      {/* Affiliate perks — real FunderPro feature icons */}
      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Why join our affiliate program</h2>
      <div className="grid grid-cols-2 gap-3">
        <PerkTile icon="Fastest Rewards" title="Fast payouts" body="Get paid every week, no delays" />
        <PerkTile icon="Multiple Accounts" title="Up to 5 linked" body="Track referrals across all your channels" />
        <PerkTile icon="Transparent" title="Transparent tracking" body="See every click and signup in real time" />
        <PerkTile icon="Simple Rules" title="Simple rules" body="No quotas, no minimum thresholds" />
      </div>

      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Account</h2>
      <div className="space-y-3">
        <FormField label="Email" name="email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" />
        <FormField label="Full name" name="full_name" value={fullName} onChange={setFullName} placeholder="Your name" />
      </div>

      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Coupon & code</h2>
      <div className="space-y-3">
        <FormField label="Discount coupon" name="discount" value={discountCoupon} onChange={setDiscountCoupon} placeholder="dsc-cpn" />
        <div>
          <FormField label="Affiliate code" name="aff_code" value={affCode} onChange={(v) => { setAffCode(v); setCodeAvailable(null); }} placeholder="aff-code" />
          {checking && <p className="mt-1 px-1 text-[12px] text-tf-text-3">Checking…</p>}
          {!checking && codeAvailable === true && <p className="mt-1 px-1 text-[12px] text-tf-profit">✓ This code is available.</p>}
          {!checking && codeAvailable === false && <p className="mt-1 px-1 text-[12px] text-tf-loss">✗ This code is already taken.</p>}
        </div>
      </div>

      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">About you</h2>
      <div className="space-y-3">
        <FormField label="Social link" name="social" type="url" value={socialLink} onChange={setSocialLink} placeholder="Facebook, Instagram, Telegram or X" />
        <TextArea label="Information" name="info" value={info} onChange={setInfo} placeholder="Give us more information about you…" rows={6} />
      </div>

      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Agreement</h2>
      <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <ToggleRow checked={accepted} onChange={setAccepted} label="Accept agreement" id="agreement" />
      </div>

      {result && (
        <div className="mt-4">
          <ErrorBox>{result.msg}</ErrorBox>
        </div>
      )}

      <div className="mt-6">
        <PrimaryButton onClick={submit} loading={submitting} testId="affiliate-submit">Send request</PrimaryButton>
      </div>
    </div>
  );
}

function PerkTile({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="rounded-[16px] border border-tf-border bg-white p-4" data-testid={`perk-${icon.toLowerCase().replace(/\s+/g, '-')}`}>
      <FeatureIcon name={icon} size={40} />
      <div className="mt-2.5 text-[15px] font-semibold text-tf-text-1">{title}</div>
      <div className="mt-0.5 text-[12px] text-tf-text-3">{body}</div>
    </div>
  );
}
